import { cn } from '@/lib/cn'
import { Link } from '@tanstack/react-router'
import { ChevronRight } from 'lucide-react'
import { Fragment, type ReactNode } from 'react'

export interface Breadcrumb {
  label: string
  to?: string
}

interface Props {
  title: string
  description?: string
  actions?: ReactNode
  breadcrumbs?: Breadcrumb[]
  className?: string
}

export function PageHeader({ title, description, actions, breadcrumbs, className }: Props) {
  return (
    <div className={cn('flex flex-col gap-2 pb-2', className)}>
      {breadcrumbs && breadcrumbs.length > 0 ? (
        <nav aria-label="Breadcrumb" className="flex items-center gap-1 text-xs text-muted-foreground">
          {breadcrumbs.map((b, i) => {
            const isLast = i === breadcrumbs.length - 1
            return (
              <Fragment key={`${b.label}-${i}`}>
                {b.to && !isLast ? (
                  <Link to={b.to} className="hover:text-foreground">
                    {b.label}
                  </Link>
                ) : (
                  <span
                    className={cn(isLast && 'text-foreground')}
                    aria-current={isLast ? 'page' : undefined}
                  >
                    {b.label}
                  </span>
                )}
                {!isLast && <ChevronRight className="h-3 w-3 text-foreground" />}
              </Fragment>
            )
          })}
        </nav>
      ) : null}
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-bold text-foreground md:text-3xl">
            {title}
          </h1>
          {description ? (
            <p className="text-sm text-muted-foreground">{description}</p>
          ) : null}
        </div>
        {actions ? <div className="flex items-center gap-2">{actions}</div> : null}
      </div>
    </div>
  )
}
